import { FC, useEffect, useRef, useState } from 'react'

import { LegalFormDisclaimer } from 'shared/ui/legal-form-disclaimer'
import { getNameError, getPhoneError, validateLeadFields } from 'shared/lib/lead-form-validation'
import { useLeadSubmission } from 'shared/hooks/use-lead-submission'

import styles from './styles.module.scss'

export const Feedback: FC = () => {
    const [name, setName] = useState('')
    const [phone, setPhone] = useState('')
    const [touched, setTouched] = useState({ name: false, phone: false })
    const [submitAttempted, setSubmitAttempted] = useState(false)
    const [consentAccepted, setConsentAccepted] = useState(false)
    const [sent, setSent] = useState(false)
    const formSurfaceRef = useRef<HTMLDivElement>(null)
    const successRef = useRef<HTMLHeadingElement>(null)
    const [successHeight, setSuccessHeight] = useState<number>()
    const { pending, error: submitError, submit } = useLeadSubmission()
    useEffect(() => { if (sent) successRef.current?.focus() }, [sent])

    const fieldErrors = validateLeadFields(name, phone)
    const nameError = getNameError(name, touched.name || submitAttempted)
    const phoneError = getPhoneError(phone, touched.phone || submitAttempted)
    const consentError = submitAttempted && !consentAccepted ? 'Подтвердите согласие.' : null

    const onSubmit = async () => {
        setSubmitAttempted(true)
        if (fieldErrors.name || fieldErrors.phone || !consentAccepted) return

        const submittedHeight = formSurfaceRef.current?.getBoundingClientRect().height
        const submitted = await submit({
            name: name.trim(),
            phone: phone.trim(),
            course: 'Нужно помочь выбрать курс',
        })
        if (submitted) {
            setSuccessHeight(submittedHeight)
            setSent(true)
        }
    }

    return (
        <section className={styles.feedback} id="feedback" aria-labelledby="feedback-title">
            <div className={styles.container}>
                <div className={styles.copy}>
                    <h2 id="feedback-title">Поможем выбрать курс</h2>
                    <p>Оставьте имя и телефон. Свяжемся, чтобы обсудить обучение.</p>
                    <a className={styles.phone} href="tel:+79953334456">+7 995 333 44 56</a>
                </div>
                <div ref={formSurfaceRef} className={styles.formSurface} style={sent ? { minHeight: successHeight } : undefined}>
                    {sent ? (
                        <div className={styles.successState} role="status">
                            <h3 ref={successRef} tabIndex={-1}>Заявка отправлена.</h3>
                            <p>Свяжемся с вами, чтобы обсудить обучение.</p>
                        </div>
                    ) : (
                        <form className={styles.form} onSubmit={(event) => { event.preventDefault(); void onSubmit() }} noValidate>
                            <div className={styles.field}>
                                <label htmlFor="feedback-name">Имя</label>
                                <input
                                    id="feedback-name"
                                    name="name"
                                    type="text"
                                    autoComplete="given-name"
                                    value={name}
                                    disabled={pending}
                                    onChange={(event) => setName(event.currentTarget.value)}
                                    onBlur={() => setTouched((current) => ({ ...current, name: true }))}
                                    aria-invalid={Boolean(nameError)}
                                    aria-describedby={nameError ? 'feedback-name-error' : undefined}
                                />
                                <p id="feedback-name-error" className={`${styles.messageSlot} ${styles.fieldError}`} data-reserve={getNameError('x'.repeat(81), true) ?? ''} aria-hidden={!nameError}><span>{nameError}</span></p>
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="feedback-phone">Телефон</label>
                                <input
                                    id="feedback-phone"
                                    name="phone"
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel"
                                    value={phone}
                                    disabled={pending}
                                    onChange={(event) => setPhone(event.currentTarget.value)}
                                    onBlur={() => setTouched((current) => ({ ...current, phone: true }))}
                                    aria-invalid={Boolean(phoneError)}
                                    aria-describedby={phoneError ? 'feedback-phone-error' : undefined}
                                />
                                <p id="feedback-phone-error" className={`${styles.messageSlot} ${styles.fieldError}`} data-reserve={getPhoneError('', true) ?? ''} aria-hidden={!phoneError}><span>{phoneError}</span></p>
                            </div>
                            <div className={styles.consent}>
                                <LegalFormDisclaimer variant="dark" consentChecked={consentAccepted} onConsentChange={setConsentAccepted} disabled={pending} />
                                <p className={`${styles.messageSlot} ${styles.fieldError}`} data-reserve="Подтвердите согласие." aria-hidden={!consentError} role={consentError ? 'alert' : undefined}><span>{consentError}</span></p>
                            </div>
                            <button type="submit" className={styles.submitButton} disabled={pending}>
                                {pending ? 'Отправляем…' : 'Отправить заявку'}
                            </button>
                            <p className={`${styles.messageSlot} ${styles.submitError}`} data-reserve="Не получили подтверждение заявки. Попробуйте ещё раз." aria-hidden={!submitError} role={submitError ? 'alert' : undefined}><span>{submitError ? 'Не получили подтверждение заявки. Попробуйте ещё раз.' : null}</span></p>
                        </form>
                    )}
                </div>
            </div>
        </section>
    )
}
